import React, { useEffect, useState } from 'react';
import {
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Stack,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useGetRelatedDirects } from '../api/getRelatedDirects';
import { RelatedDirectsListItem } from './RelatedDirectsListItem';
import { Pagination } from 'components/Pagination';
import { usePageSize } from 'hook/usePageSize';
import { Search } from 'components/Search';
import { useDebouncedCallback } from 'use-debounce';
import { NotFound } from 'components/NotFound/NotFound';
import { useAuth } from 'lib/auth';
import { Loader } from 'components/Loader';
import { useGetRelatedJobAds } from '../api/getRelatedJobAds';
import { RelatedJobAdsListItem } from './RelatedJobAdsListItem';
import { useGetRelatedJobAdsBySlug } from '../api/getRelatedJobAdsBySlug';

export const JobAdsTab = ({ id, currentTab, companyType, jobAdsCount, slug = null }) => {
  const jobAdsTabName = "job_ads";
  const { t } = useTranslation();
  const { account, user } = useAuth();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePageSize();
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    includeInactive: false,
    acceptingOffers: account?.super_admin ? false : true,
    sortBy: 'dateCreated',
  });
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(null);

  const {
    data: { jobAds: jobAdsByID, total: totalByID },
    refetch: refetchJobAds,
    isFetching: isFetchingByID,
  } = useGetRelatedJobAds(id, {
    search,
    ...filters,
    page: page - 1,
    limit: pageSize,
    queryOptions: {
      onSettled: () => {
        clearTimeout(timer);
        setTimeout(() => setLoading(false), 1000);
      },
      enabled: currentTab === jobAdsTabName && !slug
    },
  });

  const {
    data: { jobAds: jobAdsBySlug, total: totalBySlug },
    refetch: refetchJobAdsBySlug,
    isFetching: isFetchingByslug,
  } = useGetRelatedJobAdsBySlug(slug, {
    search,
    ...filters,
    page: page - 1,
    limit: pageSize,
    queryOptions: {
      onSettled: () => {
        clearTimeout(timer);
        setTimeout(() => setLoading(false), 1000);
      },
      enabled: currentTab === jobAdsTabName && !!slug
    },
  });

  const handlePageChange = (_, page) => setPage(page);
  const handleFilterUpdate = ({ target: { name, checked } }) => {
    setFilters((filters) => {
      return { ...filters, [name]: checked };
    });
  };
  const debouncedSearchChange = useDebouncedCallback((search) => setSearch(search), 300);
  const handleSearchChange = ({ target: { value: search } }) => {
    debouncedSearchChange(search);
  };

  useTabReset();
  useRefetchJobAds();

  const isFetching = isFetchingByID || isFetchingByslug;
  const jobAds = slug ? jobAdsBySlug : jobAdsByID;
  const total = slug ? totalBySlug : totalByID;

  useEffect(() => {
    if (isFetching) {
      const timeoutId = setTimeout(() => setLoading(true), 500);
      setTimer(timeoutId);
    } else {
      clearTimeout(timer);
    }

    return () => clearTimeout(timer);
  }, [isFetching]);

  if (currentTab !== jobAdsTabName) return null;

  return (
    <>
      <Stack gap={2} height={1}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} width={1}>
          <Search handleSearchChange={handleSearchChange} placeholder={t('website:publicProfile.jobAds.searchPlaceholder')} sx={{minWidth:200, width: 270}} />
        </Stack>
        <Divider />

        {isFetching && !loading ? 
        (
          <></>
        ) :
        (!isFetching || (isFetching && loading)) ?
        (
          <>
          {loading && (
            <Loader />
          )}

          {!loading && (
            <>
            <Stack gap={3} flex={1}>
              {(!jobAds?.length || jobAds?.length === 0) && (
                <NotFound
                    title={t('website:publicProfile.jobAds.emptyTitle')}
                    caption={
                    search
                        ? t('website:publicProfile.jobAds.emptyCaption')
                        : ''
                    }
                    wrapperSx={{ minHeight: "25vh", height: 1, justifyContent: 'center' }}
                />
              )}

              {jobAds.map((jobAd) => (
                <RelatedJobAdsListItem
                  key={jobAd.ad_id}
                  companyID={id}
                  companyType={companyType}
                  {...jobAd}
                />
              ))}
            </Stack>

            <Stack direction="row" justifyContent="center">
              <Pagination
                page={page}
                handlePageChange={handlePageChange}
                itemCount={total}
                itemsPerPage={pageSize}
                onItemsPerPageChange={setPageSize}
                itemsPerPageLabel={t('website:common.pagination.perPage')}
                prevLabel={t('website:common.pagination.prev')}
                nextLabel={t('website:common.pagination.next')}
              />
            </Stack>
        </>
          )}
        </>
        ) :
        (
          <></>
        )}
      </Stack>
    </>
  );

   function useRefetchJobAds() {
    useEffect(() => {
      if (currentTab === jobAdsTabName) {
        if (slug) {
          refetchJobAdsBySlug();
        } else {
          refetchJobAds();
        }
      }
    }, [page, pageSize, filters, search]);
  }

  function useTabReset() {
    useEffect(() => {
      if (currentTab !== jobAdsTabName) {
        setPage(1);
        setSearch('');
        handleFilterUpdate({target: {name: "includeInactive", checked: false}});
      }
    }, [currentTab]);
  }
};
